// The case's reel (spec web/case-opening): a strip of cards that slides past a centre marker, fast
// at first and easing to a stop on the card the server drew. The browser never picks: it only
// builds the strip around the server's answer, from the same published odds, and animates to it.

/** A card on the strip: a theme family, or the jackpot. */
export interface ReelCard {
  /** A family's id, or "jackpot". */
  id: string;
  tier: Tier;
}

export type Tier = "common" | "rare" | "epic" | "legendary" | "mythic" | "jackpot";

/** A tier's chance in percent, and the family ids it holds (none for the jackpot). */
export interface Odds {
  tier: Tier;
  percent: number;
  families: readonly string[];
}

/** How long the reel turns, its easing (fast, then a long settle), and where the winner sits. */
export const SPIN_MS = 5600;
export const EASING = [0.12, 0.8, 0.2, 1] as const;
export const WINNER_AT = 48;
/** Cards after the winner, so the strip runs on past the marker. */
const TAIL = 6;

/** A number in [0, 1) from the browser's cryptographic source, as a fair draw for filler cards. */
export function randomUnit(): number {
  const word = new Uint32Array(1);
  crypto.getRandomValues(word);
  return (word[0] ?? 0) / 2 ** 32;
}

/** The card a tier shows: one of its families, or the jackpot; none for a theme tier with no family. */
function tierCard(odds: Odds, random: () => number): ReelCard | undefined {
  if (odds.tier === "jackpot") return { id: "jackpot", tier: "jackpot" };
  const id = odds.families[Math.floor(random() * odds.families.length)];
  return id === undefined ? undefined : { id, tier: odds.tier };
}

/**
 * A filler card drawn by the published odds: nothing rare is staged beside the winner. A theme tier
 * with no family to show is left out of the draw; with nothing to show at all, there is no filler.
 */
export function fillerCard(odds: readonly Odds[], random: () => number = randomUnit): ReelCard | undefined {
  const shown = odds.filter((each) => each.tier === "jackpot" || each.families.length > 0);
  let left = random() * shown.reduce((sum, each) => sum + each.percent, 0);
  for (const each of shown) {
    left -= each.percent;
    if (left < 0) return tierCard(each, random);
  }
  const last = shown.at(-1);
  return last === undefined ? undefined : tierCard(last, random);
}

/** The strip: filler from the odds, the server's card at `WINNER_AT`, and a tail after it. */
export function buildStrip(
  winner: ReelCard,
  odds: readonly Odds[],
  random: () => number = randomUnit,
): ReelCard[] {
  const cards = Array.from({ length: WINNER_AT + TAIL }, () => fillerCard(odds, random) ?? winner);
  cards[WINNER_AT] = winner;
  return cards;
}

/**
 * Where the strip stops: the winner's centre under the marker, give or take part of a card, so
 * two openings never land on the same pixel. `step` is a card's width and the gap after it.
 */
export function landingOffset(
  view: number,
  card: number,
  step: number,
  random: () => number = randomUnit,
): number {
  const jitter = (random() - 0.5) * 0.7 * card;
  return -(WINNER_AT * step + card / 2 + jitter - view / 2);
}

/** CSS's cubic-bezier(x1, y1, x2, y2) as a function of time, to know which card passes the marker. */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let round = 0; round < 8; round += 1) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-6) return sampleY(t);
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= error / slope;
    }
    let low = 0;
    let high = 1;
    t = x;
    while (high - low > 1e-6) {
      if (sampleX(t) < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return sampleY(t);
  };
}

/** The card under the marker at `progress` (0 to 1) of the spin: a new one is a tick. */
export function cardAtMarker(progress: number, view: number, end: number, step: number): number {
  const eased = cubicBezier(...EASING)(progress);
  return Math.floor((view / 2 - end * eased) / step);
}
